import { loginRequest } from "@/authConfig";
import {
	getColleagues,
	getDepartment,
	getUserCheck,
	getUserManager,
	graphPhoto,
} from "@/services/auth-functions";
import {
	BrowserAuthError,
	InteractionRequiredAuthError,
	InteractionStatus,
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import React, { useEffect, useReducer } from "react";
import { useMutation } from "react-query";
import {
	GET_MANAGER,
	GET_PROFILE,
	GET_PROFILE_PHOTO,
	GET_USER_CHECK,
	GET_USER_PERMISSIONS,
	GET_USER_TOKEN,
	MANAGER_DIRECT_REPORTS,
} from "../types";
import { UserContext } from "./UserContext";
import UserReducer from "./UserReducer";

const UserState: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { instance, accounts, inProgress } = useMsal();
	const [timesErrorIsCalled, setTimesErrorIsCalled] = React.useState(0);

	const initialState = {
		check: null,
		permissions: null,
		department: null,
		manager: null,
		directReports: [],
		accessToken: null,
		profilePhoto: null,
	};

	const [state, dispatch] = useReducer(UserReducer, initialState);

	const handleAccessTokenReceived = (accessToken: string) => {
		console.log("Access Token Received:", accessToken);
		dispatch({ type: GET_USER_TOKEN, payload: accessToken });
		getUserCheckMutation.mutate(accessToken);
		getGraphPhotoMutation.mutate(accessToken);
		getDepartmentMutation.mutate(accessToken);
		getManagerMutation.mutate(accessToken);
	};

	const getUserCheckMutation = useMutation({
		mutationKey: "get_user_check",
		mutationFn: (accessToken: string) => getUserCheck(accessToken),
		onSuccess: (response) => {
			console.log("User Check Response:", response);
			dispatch({ type: GET_USER_CHECK, payload: response.user });
			dispatch({ type: GET_USER_PERMISSIONS, payload: response.permissions });
		},
		onError: () => {
			if (timesErrorIsCalled <= 5) handleUserTokenRenewal();
		},
		retry: false,
	});

	const getGraphPhotoMutation = useMutation({
		mutationKey: "get_graph_photo",
		mutationFn: (accessToken: string) => graphPhoto(accessToken),
		onSuccess: (response) => {
			dispatch({ type: GET_PROFILE_PHOTO, payload: response });
		},
		onError: () => {
			dispatch({ type: GET_PROFILE_PHOTO, payload: null });
		},
	});

	const getDepartmentMutation = useMutation({
		mutationKey: "get_department",
		mutationFn: (accessToken: string) => getDepartment(accessToken),
		onSuccess: (response) => {
			dispatch({ type: GET_PROFILE, payload: response });
		},
		onError: () => {
			dispatch({ type: GET_PROFILE, payload: null });
		},
	});

	const getManagerMutation = useMutation({
		mutationKey: "get_manager",
		mutationFn: (accessToken: string) => getUserManager(accessToken),
		onSuccess: (response) => {
			dispatch({ type: GET_MANAGER, payload: response.data });
			getDirectReportsQuery.mutate({
				accessToken: response.token,
				manager: response.data.userPrincipalName,
			});
		},
		onError: () => {
			dispatch({ type: GET_MANAGER, payload: null });
		},
	});

	const getDirectReportsQuery = useMutation({
		mutationKey: "get_direct_reports",
		mutationFn: ({
			accessToken,
			manager,
		}: {
			accessToken: string;
			manager: string;
		}) => getColleagues(accessToken, manager),
		onSuccess: (response) => {
			dispatch({ type: MANAGER_DIRECT_REPORTS, payload: response.value });
		},
		onError: () => {
			dispatch({ type: MANAGER_DIRECT_REPORTS, payload: [] });
		},
	});

	const handleUserTokenRenewal = () => {
		setTimesErrorIsCalled((prev) => prev + 1);
		const accessTokenRequest = {
			...loginRequest,
			account: accounts[0],
		};
		if (inProgress === InteractionStatus.None) {
			instance
				.acquireTokenSilent(accessTokenRequest)
				.then((accessTokenResponse) => {
					// Acquire token silent success
					let accessToken = accessTokenResponse.accessToken;
					// Call your API with token
					handleAccessTokenReceived(accessToken);
				})
				.catch((error) => {
					if (error instanceof InteractionRequiredAuthError) {
						instance.acquireTokenRedirect(accessTokenRequest);
					}
					//? Handle the user redirecting to the login page manually
					if (error instanceof BrowserAuthError) {
						window.location.href = "/";
					}
					console.log(error);
				});
		}
	};

	useEffect(() => {
		handleUserTokenRenewal();
	}, [instance, accounts, inProgress]);

	return <UserContext.Provider value={state}>{children}</UserContext.Provider>;
};

export default UserState;
